/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.03.16 at 18:07
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { asArray, closest, forEach, isArray, isBool, isFunction, isNumber, isString, isUndefined } from '@labor-digital/helferlein';
/**
 * Prepares a css selector by checking if it begins with a magic prefix and automatically extends it
 * to match the according selector
 * @param selector
 */
export function prepareCssSelector(selector) {
    return (selector + '')
        // Replace selector modifiers like: ".class:@selector"
        .replace(/(^|\w):@([^\s]*?)(\[|:|\s|$)/g, '$1[data-ref="$2"]$3')
        // Replace stand-alone selectors like ".class @selector"
        .replace(/(^|\s|\()@([^\s]*?)(\[|\)|:|\s|$)/g, '$1*[data-ref="$2"]$3');
}
/**
 * Resolves the "closest" parent element that matches the given selector.
 *
 * @param mount The mount element to resolve the request in
 * @param selector any query selector to find your element with. As a "magic" helper you can provide "@my-ref"
 * that will be converted into '*[data-ref="my-ref"]' internally before the query is resolved.
 * @param to The pivot element from which the closest element should be resolved
 * @param includeParents By default only elements inside the given mount are resolved.
 * If you set this to true, results outside the bounds of the mount will be returned as well.
 */
export function findClosest(mount, selector, to, includeParents) {
    selector = prepareCssSelector(selector);
    var closestEl = closest(selector, to);
    if (includeParents || !closestEl) {
        return closestEl;
    }
    var mountTag = mount.tagName;
    if (closest(mountTag, closestEl) !== mount) {
        return null;
    }
    return closestEl;
}
/**
 * Internal helper to resolve the "find" request inside a single mount
 * @param mount The mount element to resolve the request in
 * @param selector any query selector to find your element with. As a "magic" helper you can provide "@my-ref"
 * that will be converted into '*[data-ref="my-ref"]' internally before the query is resolved.
 * @param multiple By default only a single element is returned "querySelector", if set to true "querySelectorAll"
 * is used instead.
 * @param deepOrPivot Either a boolean value to define the "deep" property, or an html element defining the
 * html element which is used as pivot element for the lookup
 * @param deep By default only elements inside the current mount are resolved, but children
 * are ignored while retrieving elements. If you set this to true, even elements in child-mounts are returned
 * @internal
 */
export function findElement(mount, selector, multiple, deepOrPivot, deep) {
    selector = prepareCssSelector(selector);
    var pivot = deepOrPivot instanceof HTMLElement ? deepOrPivot : mount;
    deep = isBool(deepOrPivot) ? deepOrPivot : deep;
    var mountTag = mount.tagName;
    var list = pivot.querySelectorAll(selector);
    // We need to check if an element is currently inside this elements root in order
    // to simulate the behaviour of a component.
    if (!deep) {
        var filtered_1 = [];
        forEach(list, function (el) {
            var closestEl = closest(mountTag, el);
            if (!closestEl) {
                return;
            }
            if (closestEl === mount ||
                // We also want to include child-mounts that are in our domain
                closestEl === el && closest(mountTag, closestEl.parentElement) === mount) {
                filtered_1.push(el);
                if (!multiple) {
                    return false;
                }
            }
        });
        return filtered_1;
    }
    else {
        return asArray(list);
    }
}
/**
 * Internal helper to translate the given event target into an array of valid component proxy event targets
 * @param target
 * @param deep
 */
export function resolveEventTarget(target, deep) {
    if (isUndefined(target)) {
        return [this.$el];
    }
    if (isFunction(target)) {
        return resolveEventTarget.call(this, target.call(this), deep);
    }
    if (target === true) {
        return [this.$app.di.eventBus];
    }
    else if (isString(target)) {
        return this.$findAll(target, deep);
    }
    else if (isNumber(target.length) && !target.addEventListener) {
        return asArray(target);
    }
    return [target];
}
/**
 * Internal helper to translate all possible event target definitions and event name lists
 * and bind or unbind them using the provided component proxy instance
 *
 * @param proxy
 * @param target
 * @param deep
 * @param events
 * @param listener
 * @param method
 */
export function runOnEventProxy(proxy, target, deep, events, listener, method) {
    forEach(resolveEventTarget.call(this, target, deep), function (el) {
        forEach(isArray(events) ? events : [events], function (event) {
            proxy[method](el, event, listener);
        });
    });
}
/**
 * Internal helper to keep the event binding/unbinding code inside a single bit as DRY as possible
 * @param method
 * @param a
 * @param b
 * @param c
 */
export function bitEventActionWrap(method, a, b, c) {
    var hasTarget = !isUndefined(c);
    var event = hasTarget ? b : a;
    var listener = hasTarget ? c : b;
    runOnEventProxy.call(this, this.$proxy, hasTarget ? a : undefined, false, event, listener, method);
    return this;
}
/**
 * Utility to declare dynamic getters on the given target that are all handled by a "magic" handler method
 *
 * @param target The object to register the getters on
 * @param handler The handler method is called when a getter is called. It receives the key of the property
 * to resolve and must return the content for it
 * @param initialKeys Optional list of initial keys that should be registered
 */
export function makeGetterProvider(target, handler, initialKeys) {
    var registeredKeys = [];
    var h = {
        add: function (key) {
            if (registeredKeys.indexOf(key) !== -1) {
                return;
            }
            registeredKeys.push(key);
            Object.defineProperty(target, key, {
                get: function () {
                    return handler(key);
                }
            });
        }
    };
    if (initialKeys) {
        forEach(initialKeys, function (k) { return h.add(k); });
    }
    return h;
}
/**
 * Small for-each helper that waits on promises returned by its callback
 * @param list
 * @param callback
 */
export function awaitingForEach(list, callback) {
    return __awaiter(this, void 0, void 0, function () {
        var i;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < list.length)) return [3 /*break*/, 4];
                    return [4 /*yield*/, callback(list[i], i)];
                case 2:
                    _a.sent();
                    _a.label = 3;
                case 3:
                    i++;
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/];
            }
        });
    });
}
//# sourceMappingURL=util.js.map